package com.mgacreative.hotel.feature.reservation.ui

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.mgacreative.hotel.core.network.SupabaseNetworkClient
import com.mgacreative.hotel.core.tenant.TenantSession
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch
import kotlinx.datetime.*
import kotlinx.serialization.Serializable
import com.mgacreative.hotel.domain.repository.ReservationRepository
import com.mgacreative.hotel.domain.repository.RoomRepository

@Serializable
data class Room(val id: String, val number: String, val status: String = "AVAILABLE")

@Serializable
data class ReservationItem(
    val id: String,
    val room_id: String,
    val guest_name: String,
    val start_date: String, // YYYY-MM-DD
    val end_date: String,   // YYYY-MM-DD
    val status: String
)

class ReservationViewModel(
    private val reservationRepository: ReservationRepository,
    private val roomRepository: RoomRepository,
    private val tenantSession: TenantSession
) : ViewModel() {

    private val _rooms = MutableStateFlow<List<Room>>(emptyList())
    val rooms: StateFlow<List<Room>> = _rooms.asStateFlow()

    private val _reservations = MutableStateFlow<List<ReservationItem>>(emptyList())
    val reservations: StateFlow<List<ReservationItem>> = _reservations.asStateFlow()

    private val _errorMessage = MutableStateFlow<String?>(null)
    val errorMessage: StateFlow<String?> = _errorMessage.asStateFlow()

    fun loadData() {
        viewModelScope.launch {
            try {
                val tenantId = tenantSession.currentTenantId ?: return@launch
                
                // Supabase'den gerçek odaları çek
                val fetchedRooms = roomRepository.getRooms()
                _rooms.value = fetchedRooms.map { Room(it.id ?: "", it.roomNumber, it.status) }
                
                // Supabase'den gerçek rezervasyonları çek
                val fetchedReservations = reservationRepository.getReservations()
                _reservations.value = fetchedReservations.map { res ->
                    ReservationItem(
                        id = res.id ?: "",
                        room_id = res.roomId ?: "",
                        guest_name = res.guests?.firstName + " " + res.guests?.lastName, // Joined guest data
                        start_date = res.checkInDate,
                        end_date = res.checkOutDate,
                        status = res.status
                    )
                }
            } catch (e: Exception) {
                _errorMessage.value = "Veriler yüklenirken hata oluştu: ${e.message}"
            }
        }
    }

    // Acceptance Criteria: No overlapping reservations in the same room, and room cannot be in MAINTENANCE
    private fun isOverlapOrBlocked(roomId: String, start: LocalDate, end: LocalDate, excludeResId: String? = null): Boolean {
        // 1. Check if room is in MAINTENANCE
        val room = _rooms.value.find { it.id == roomId }
        if (room?.status == "MAINTENANCE") {
            return true
        }

        // 2. Check overlap
        return _reservations.value.any {
            if (it.id == excludeResId || it.room_id != roomId || it.status == "CANCELLED") return@any false
            
            val existingStart = LocalDate.parse(it.start_date)
            val existingEnd = LocalDate.parse(it.end_date)
            
            // Overlap logic
            start < existingEnd && end > existingStart
        }
    }

    fun moveReservation(resId: String, newRoomId: String, newStart: LocalDate, newEnd: LocalDate) {
        if (isOverlapOrBlocked(newRoomId, newStart, newEnd, excludeResId = resId)) {
            _errorMessage.value = "HATA: Seçilen tarihlerde odaya ait başka bir rezervasyon var veya oda BAKIM (Maintenance) durumunda."
            return
        }

        viewModelScope.launch {
            try {
                // val updateData = mapOf("room_id" to newRoomId, "check_in_date" to newStart.toString(), "check_out_date" to newEnd.toString())
                // supabaseClient.client.postgrest["reservations"].update(updateData) { filter { eq("id", resId) } }
                val updated = _reservations.value.map {
                    if (it.id == resId) {
                        it.copy(room_id = newRoomId, start_date = newStart.toString(), end_date = newEnd.toString())
                    } else it
                }
                _reservations.value = updated
                _errorMessage.value = null
            } catch (e: Exception) {
                _errorMessage.value = "Güncelleme hatası: ${e.message}"
            }
        }
    }

    fun createReservation(roomId: String, start: LocalDate, end: LocalDate, guestName: String = "Yeni Misafir") {
        if (isOverlapOrBlocked(roomId, start, end)) {
            _errorMessage.value = "HATA: Seçilen tarihlerde odaya ait başka bir rezervasyon var veya oda BAKIM (Maintenance) durumunda."
            return
        }
        viewModelScope.launch {
            try {
                // Gerçek insert işlemi
                // supabaseClient.client.postgrest["reservations"].insert(...)
                
                val newRes = ReservationItem(
                    id = "res_${kotlin.random.Random.nextInt()}",
                    room_id = roomId,
                    guest_name = guestName,
                    start_date = start.toString(),
                    end_date = end.toString(),
                    status = "CONFIRMED"
                )
                _reservations.value = _reservations.value + newRes
                _errorMessage.value = null
            } catch (e: Exception) {
                _errorMessage.value = "Rezervasyon oluşturma hatası: ${e.message}"
            }
        }
    }

    fun cancelReservation(resId: String) {
        viewModelScope.launch {
            try {
                // supabaseClient.client.postgrest["reservations"].update(mapOf("status" to "CANCELLED")) { filter { eq("id", resId) } }
                _reservations.value = _reservations.value.map {
                    if (it.id == resId) it.copy(status = "CANCELLED") else it
                }
            } catch (e: Exception) {
                _errorMessage.value = "İptal işlemi başarısız: ${e.message}"
            }
        }
    }

    fun clearError() {
        _errorMessage.value = null
    }
}

